import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField, Input, Select, TableCell, TableRow, Text, toNumberString } from '@client/toolkit';

import { EconomyActiveField } from './economy-active-field.component';
import { EconomyRowActions } from './economy-row-actions.component';
import { ECONOMY_FIELD_SIZE, ECONOMY_VALUE_CLASS } from '../economy-theme';
import { ALLOCATION_METHODS, COST_CLASSIFICATIONS } from '../mock/portfolio-economy.mock';
import { GroupAccountsFormPath, GroupAccountsFormValues as FormValues } from '../portfolio-economy-settings.form';

export interface GroupAccountRowProps {
  index: number;
  editing: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRemove: (index: number) => void;
}

/**
 * A `<input type="number">` edited through react-hook-form's controller lands in form
 * state as a string, so the formatted readout has to coerce rather than type-check.
 */
const toDisplayAmount = (value: unknown) => {
  if (value === '' || value == null) return undefined;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : undefined;
};

export function GroupAccountRow({
  index,
  editing,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onRemove,
}: GroupAccountRowProps) {
  const { t } = useTranslation('economy');
  const { control, trigger, getFieldState, formState } = useFormContext<FormValues>();

  const account = useWatch({ control, name: `groupAccounts.${index}` });
  const amount = account?.amount;
  const accountNumber = account?.accountNumber;

  /** Distinguishes this row's controls for screen readers; falls back to its position. */
  const row = typeof accountNumber === 'string' && accountNumber.trim() !== '' ? accountNumber : String(index + 1);

  /**
   * The uniqueness test spans rows, but react-hook-form only rewrites the error of the
   * field being edited — without this, resolving a collision leaves the other row red.
   * Only runs once the array already has an error, to stay lazy before first submit.
   */
  const revalidateAccountNumbers = () => {
    if (getFieldState('groupAccounts', formState).error != null) void trigger('groupAccounts');
  };

  const amountReadout = toNumberString(toDisplayAmount(amount), '—', { unit: 'kr', maximumFractionDigits: 0 });

  return (
    <TableRow verticalAlign="middle">
      <TableCell>
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>{accountNumber}</span>
        ) : (
          <FormField<FormValues, GroupAccountsFormPath>
            name={`groupAccounts.${index}.accountNumber`}
            render={({ field }) => (
              <Input
                {...field}
                onChange={(event) => {
                  field.onChange(event);
                  revalidateAccountNumbers();
                }}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.groupAccounts.rowFields.account', { row })}
              />
            )}
          />
        )}
      </TableCell>

      <TableCell>
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>{account?.name}</span>
        ) : (
          <FormField<FormValues, GroupAccountsFormPath>
            name={`groupAccounts.${index}.name`}
            render={({ field }) => (
              <Input
                {...field}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.groupAccounts.rowFields.name', { row })}
              />
            )}
          />
        )}
      </TableCell>

      <TableCell>
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>
            {account?.classification && t(`classification.${account.classification}`)}
          </span>
        ) : (
          <FormField<FormValues, GroupAccountsFormPath>
            name={`groupAccounts.${index}.classification`}
            render={({ field }) => (
              <Select
                {...field}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.groupAccounts.rowFields.classification', { row })}
              >
                {COST_CLASSIFICATIONS.map((classification) => (
                  <option key={classification} value={classification}>
                    {t(`classification.${classification}`)}
                  </option>
                ))}
              </Select>
            )}
          />
        )}
      </TableCell>

      <TableCell align="right">
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>{amountReadout}</span>
        ) : (
          <div className="flex items-center justify-end gap-2">
            {/* The input takes the slack; the unit keeps its width or it gets clipped. */}
            <div className="min-w-0 flex-1">
              <FormField<FormValues, GroupAccountsFormPath>
              name={`groupAccounts.${index}.amount`}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    size={ECONOMY_FIELD_SIZE}
                    aria-label={t('settings.groupAccounts.rowFields.amount', { row })}
                    className="text-right"
                  />
                )}
              />
            </div>
            <Text className="shrink-0 whitespace-nowrap text-xs">{t('settings.groupAccounts.currency')}</Text>
          </div>
        )}
      </TableCell>

      <TableCell>
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>
            {account?.method && t(`allocationMethod.${account.method}`)}
          </span>
        ) : (
          <FormField<FormValues, GroupAccountsFormPath>
            name={`groupAccounts.${index}.method`}
            render={({ field }) => (
              <Select
                {...field}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.groupAccounts.rowFields.method', { row })}
              >
                {ALLOCATION_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {t(`allocationMethod.${method}`)}
                  </option>
                ))}
              </Select>
            )}
          />
        )}
      </TableCell>

      <TableCell>
        <EconomyActiveField<FormValues>
          section="settings.groupAccounts"
          row={row}
          name={`groupAccounts.${index}.active`}
          active={account?.active}
          editing={editing}
        />
      </TableCell>

      <TableCell align="right">
        {editing && (
          <EconomyRowActions
            section="settings.groupAccounts"
            row={row}
            isFirst={isFirst}
            isLast={isLast}
            onMoveUp={() => onMoveUp(index)}
            onMoveDown={() => onMoveDown(index)}
            onRemove={() => onRemove(index)}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

export default GroupAccountRow;
